/**
 * Install hint, install guide, and pending-session helpers.
 *
 * The pad is browser-first: scanning a game QR drops you straight
 * into the controller in whatever browser you have open, no install.
 *
 * Installing the PWA is the only way to a chrome-free controller on
 * iPhone (Apple gives web pages no fullscreen API). So on iPhone the
 * install path is made as painless as possible: a clear three-tap
 * guide, and the game session is preserved so the freshly-installed
 * app resumes straight into the same game with no second scan.
 */

import {
  canInstallNow, isIosSafari, isMobileViewport, isStandalone, triggerInstall,
} from './install-fullscreen.js';
import type { PairingToken } from '@forgesworn/joystick-mobile';

const PENDING_SESSION_KEY = 'pad:pendingSession';
const PENDING_WS_KEY = 'pad:pendingWs';
const PENDING_VERSION_KEY = 'pad:pendingVersion';
const HINT_DISMISSED_KEY = 'pad:installHintDismissed';

export function savePendingSession(token: PairingToken): void {
  try {
    localStorage.setItem(PENDING_SESSION_KEY, token.sessionId);
    if (token.ws) localStorage.setItem(PENDING_WS_KEY, token.ws);
    if (token.version === 2) localStorage.setItem(PENDING_VERSION_KEY, '2');
    else localStorage.removeItem(PENDING_VERSION_KEY);
  } catch { /* private browsing — fine, scanner will just ask again */ }
}

export function readPendingSession(): PairingToken | null {
  try {
    const s = localStorage.getItem(PENDING_SESSION_KEY);
    if (!s || !/^[a-z0-9_-]{4,128}$/i.test(s)) return null;
    const w = localStorage.getItem(PENDING_WS_KEY) ?? 'wss://controller.pallasite.app/';
    if (!/^wss?:\/\//.test(w)) return null;
    return { sessionId: s, ws: w, ...(localStorage.getItem(PENDING_VERSION_KEY) === '2' ? { version: 2 } : {}) };
  } catch { return null; }
}

export function clearPendingSession(): void {
  try {
    localStorage.removeItem(PENDING_SESSION_KEY);
    localStorage.removeItem(PENDING_WS_KEY);
    localStorage.removeItem(PENDING_VERSION_KEY);
  } catch { /* ignore */ }
}

/**
 * Full-screen install walkthrough — a modal sheet with the exact
 * Add-to-Home-Screen taps. The only route to a chrome-free controller
 * on iPhone, so it is spelled out plainly and reassures the user that
 * their game is held.
 */
export function openInstallGuide(): void {
  document.getElementById('install-guide')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'install-guide';
  overlay.className = 'sheet-overlay';
  const sheet = document.createElement('div');
  sheet.className = 'sheet';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'sheet-close';
  close.setAttribute('aria-label', 'close');
  close.textContent = '✕';
  const dismiss = (): void => overlay.remove();
  close.addEventListener('click', dismiss);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) dismiss(); });

  const title = document.createElement('div');
  title.className = 'sheet-title';
  title.textContent = 'PLAY FULLSCREEN';

  const intro = document.createElement('p');
  intro.className = 'install-guide-intro';
  intro.textContent = 'A controller with no browser bar takes three quick taps:';

  const steps = document.createElement('ol');
  steps.className = 'install-guide-steps';
  steps.innerHTML = `
    <li>Tap the <strong>Share</strong> button <span class="share-icon">⎙</span> in Safari's toolbar.</li>
    <li>Scroll down and choose <strong>Add to Home Screen</strong>.</li>
    <li>Tap <strong>Add</strong>, then open <strong>Joystick Pad</strong> from your Home Screen.</li>
  `;

  const reassure = document.createElement('p');
  reassure.className = 'install-guide-reassure';
  reassure.textContent = 'Your game is saved. The app resumes straight into it the moment it opens — no need to scan again.';

  const done = document.createElement('button');
  done.type = 'button';
  done.className = 'btn primary';
  done.textContent = 'GOT IT';
  done.addEventListener('click', dismiss);

  sheet.append(close, title, intro, steps, reassure, done);
  overlay.appendChild(sheet);
  (document.getElementById('app') ?? document.body).appendChild(overlay);
}

/**
 * Append a compact, dismissable "play fullscreen" hint to the host.
 * No-op when it would not help: already a standalone PWA, on desktop,
 * or previously dismissed. Never blocks play.
 *
 * iPhone gets a "show me how" button into the install guide; Android
 * gets the real, capture-able install prompt.
 */
export function renderInstallHint(host: HTMLElement): void {
  if (isStandalone()) return;        // already installed
  if (!isMobileViewport()) return;   // desktop — browser chrome is fine
  try {
    if (localStorage.getItem(HINT_DISMISSED_KEY) === '1') return;
  } catch { /* ignore — show the hint */ }

  const card = document.createElement('div');
  card.className = 'install-hint';

  const title = document.createElement('div');
  title.className = 'install-hint-title';
  title.textContent = 'PLAY FULLSCREEN';

  const body = document.createElement('div');
  body.className = 'install-hint-body';
  card.append(title, body);

  if (isIosSafari()) {
    body.textContent = 'Add Joystick Pad to your Home Screen for a controller with no browser bar.';
    const howBtn = document.createElement('button');
    howBtn.type = 'button';
    howBtn.className = 'install-hint-btn';
    howBtn.textContent = 'SHOW ME HOW';
    howBtn.addEventListener('click', () => openInstallGuide());
    card.appendChild(howBtn);
  } else if (canInstallNow()) {
    body.textContent = 'Install the controller to lose the browser bar.';
    const installBtn = document.createElement('button');
    installBtn.type = 'button';
    installBtn.className = 'install-hint-btn';
    installBtn.textContent = 'INSTALL';
    installBtn.addEventListener('click', () => { void triggerInstall(); });
    card.appendChild(installBtn);
  } else {
    body.textContent = 'Add Joystick Pad to your home screen from your browser menu for a chrome-free controller.';
  }

  const dismiss = document.createElement('button');
  dismiss.type = 'button';
  dismiss.className = 'install-hint-dismiss';
  dismiss.textContent = 'Not now';
  dismiss.addEventListener('click', () => {
    try { localStorage.setItem(HINT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    card.remove();
  });
  card.appendChild(dismiss);

  host.appendChild(card);
}
